import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

const socialMediaValidationSchema = Yup.object().shape({
	facebook: Yup.string().url('Invalid URL format').optional(),
	twitter: Yup.string().url('Invalid URL format').optional(),
	linkedin: Yup.string().url('Invalid URL format').optional(),
	instagram: Yup.string().url('Invalid URL format').optional(),
});

const SocialMediaInfo = ({ onComplete, onBack }) => {
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [initialData, setInitialData] = useState({
		facebook: '',
		twitter: '',
		linkedin: '',
		instagram: '',
	});

	const [formData, setFormData] = useState({
		facebook: '',
		twitter: '',
		linkedin: '',
		instagram: '',
	});

	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleSubmit = async ({ setSubmitting }) => {
		try {
			const response = await axios.post(
				'http://127.0.0.1:8000/hospital/social-media',
				formData,
				{
					headers: {
						'Content-Type': 'application/json',
					},
				},
			);

			if (response.status === 201) {
				setSuccess('Social Links information submitted successfully!');
				onComplete(data, true); // Call the onComplete function with the response data
				setError(''); // Reset error on successful submission
			} else {
				throw new Error(
					'Social Links information submission failed. Please try again.',
				);
			}
		} catch (error) {
			setError(
				error?.response?.data?.detail ||
					'Failed to submit social links information',
			);
		}
	};

	return (
		<motion.div
			className={css(styles.container)}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<h2 className={css(styles.title)}>Social Media and Online Presence</h2>
			{error && <div className={css(styles.error)}>{error}</div>}
			{success && <div className={css(styles.success)}>{success}</div>}
			<Formik
				initialValues={{
					facebook: initialData?.facebook || '',
					twitter: initialData?.twitter || '',
					linkedin: initialData?.linkedin || '',
					instagram: initialData?.instagram || '',
				}}
				validationSchema={socialMediaValidationSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, touched, isValid, dirty }) => (
					<Form className={css(styles.form)}>
						<div className={css(styles.fieldGroup)}>
							<label htmlFor="name" className={css(styles.label)}>
								Facebook
							</label>
							<input
								name="facebook"
								type="url"
								onChange={handleChange}
								className={css(styles.input)}
								required
							/>
							{errors.facebook && touched.facebook && (
								<div className={css(styles.error)}>{errors.facebook}</div>
							)}
						</div>
						<div className={css(styles.fieldGroup)}>
							<label htmlFor="twitter" className={css(styles.label)}>
								Twitter
							</label>
							<input
								name="twitter"
								type="url"
								onChange={handleChange}
								className={css(styles.input)}
								required
							/>
							{errors.twitter && touched.twitter && (
								<div className={css(styles.error)}>{errors.twitter}</div>
							)}
						</div>
						<div className={css(styles.fieldGroup)}>
							<label htmlFor="linkedin" className={css(styles.label)}>
								Linkedin
							</label>
							<input
								name="linkedin"
								type="url"
								onChange={handleChange}
								className={css(styles.input)}
								required
							/>
							{errors.linkedin && touched.linkedin && (
								<div className={css(styles.error)}>{errors.linkedin}</div>
							)}
						</div>
						<div className={css(styles.fieldGroup)}>
							<label htmlFor="instagram" className={css(styles.label)}>
								Instagram
							</label>
							<input
								name="instagram"
								type="url"
								onChange={handleChange}
								className={css(styles.input)}
								required
							/>
							{errors.instagram && touched.instagram && (
								<div className={css(styles.error)}>{errors.instagram}</div>
							)}
						</div>
						<div className={css(styles.buttonContainer)}>
							<motion.button
								type="submit"
								className={css(styles.button, styles.nextButton)}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								onClick={handleSubmit}
							>
								Save
							</motion.button>
							<motion.button
								type="button"
								className={css(styles.button, styles.nextButton)}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								onClick={() => {
									if (!isValid) {
										console.log('Form is invalid, staying on the same page.');
										alert(
											'Please fill in all required fields before proceeding.',
										);
										return;
									}
									console.log('Form is valid, navigating to the next section.');
									onComplete(); // Ensure this function correctly updates the state for the next section
								}}
							>
								Next
							</motion.button>
							<motion.button
								type="button"
								className={css(styles.button, styles.backButton)}
								onClick={onBack}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
							>
								Back
							</motion.button>
						</div>
					</Form>
				)}
			</Formik>
		</motion.div>
	);
};

const styles = StyleSheet.create({
	container: {
		marginBottom: '2rem',
	},
	title: {
		fontSize: '1.5rem',
		marginBottom: '1rem',
		color: '#2783F7',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
	},
	fieldGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
	},
	label: {
		marginBottom: '0.5rem',
		fontSize: '1rem',
		color: '#4a5568',
	},
	input: {
		padding: '0.5rem',
		fontSize: '1rem',
		borderRadius: '5px',
		border: '1px solid #e2e8f0',
		marginBottom: '0.5rem',
	},
	error: {
		color: 'red',
		fontSize: '0.875rem',
	},
	success: {
		color: 'green',
		fontSize: '0.875rem',
		marginBottom: '1rem',
	},
	buttonContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
		marginTop: '1rem',
	},
	button: {
		backgroundColor: '#2783F7',
		color: 'white',
		border: 'none',
		padding: '0.5rem 1rem',
		fontSize: '1rem',
		borderRadius: '5px',
		cursor: 'pointer',
		marginTop: '1rem',
	},
	nextButton: {
		backgroundColor: '#2783F7',
		color: 'white',
	},
	backButton: {
		backgroundColor: '#e2e8f0',
		color: '#4a5568',
	},
	disabledButton: {
		backgroundColor: '#a0aec0',
		cursor: 'not-allowed',
	},
});

export default SocialMediaInfo;
